import { Button, Card, Form, Input, Modal } from "antd";
import { getSenderDetails } from "api/services/FaxService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import React, { useEffect, useState } from "react";

interface NewFaxModalProps {
  isVisible: boolean;
  jobIds: string[];
  handleClose: () => void;
  onSubmit: (values: any) => Promise<void>;
}

const NewFaxModal: React.FC<NewFaxModalProps> = ({
  isVisible,
  jobIds,
  handleClose,
  onSubmit
}) => {
  const [form] = Form.useForm();
  const { accountId } = useTDAuthentication();
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (isVisible) {
      fetchSenderData();
    }
  }, [isVisible]);

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
    const errorField = errorInfo.errorFields[0]; // Get the first error field
    if (errorField) {
      setValidationError(errorField.errors[0]); // Set the validation error message
    }
  };

  const fetchSenderData = async () => {
    try {
      form.resetFields();
      const data = await getSenderDetails(accountId || "");
      const mappedData = {
        senderName: data.displayName,
        senderPhoneNumber: formatPhoneNumber(data.phone1),
        senderFaxNumber: formatPhoneNumber(data.fax),
        senderCompanyName: data.accountName,
        confidentialityNotice: data.confidentialityNotice
      };
      form.setFieldsValue(mappedData);
    } catch (error) {
      console.error("Error fetching sender data:", error);
    }
  };

  const handleModalClose = () => {
    handleClose();
    form.resetFields();
  };

  const handleFinish = async (values: any) => {
    setValidationError(null);
    const strippedValues = {
      ...values,
      senderPhoneNumber: values.senderPhoneNumber?.replace(/\D/g, ""),
      senderFaxNumber: values.senderFaxNumber?.replace(/\D/g, ""),
      recipientPhoneNumber: values.recipientPhoneNumber?.replace(/\D/g, ""),
      recipientFaxNumber: values.recipientFaxNumber?.replace(/\D/g, ""),
      jobIds: jobIds
    };

    await onSubmit(strippedValues);
    handleModalClose();
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length < 4) return cleaned;
    if (cleaned.length < 7) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const validatePhoneAndFax = (_: any, value: string) => {
    if (value) {
      const cleaned = value.replace(/\D/g, "");
      if (cleaned.length !== 10) {
        return Promise.reject(new Error("Phone/Fax number must be exactly 10 digits."));
      }
    }
    return Promise.resolve();
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const lineCount = value.split("\n").length;

    if (lineCount > 20) {
      const lines = value.split("\n").slice(0, 20).join("\n");
      form.setFieldsValue({ notes: lines });
    }
  };

  return (
    <Modal
      title="New Fax Information"
      visible={isVisible}
      onCancel={handleModalClose}
      footer={null}
      width={900}
      centered={true}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onFinishFailed={onFinishFailed}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Card
            style={{
              marginBottom: "20px",
              border: "1px solid #ddd",
              padding: "10px",
              width: "100%",
              marginRight: "12px"
            }}
            styles={{ body: { padding: 0 } }}>
            <h3 style={{ marginBottom: "12px" }}>
              <strong>Sender Information</strong>
            </h3>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "40px"
              }}>
              <div style={{ flex: 1, marginRight: "10px", width: "100%", height: "40px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px"
                  }}>
                  Name
                </label>
                <Form.Item
                  name="senderName"
                  rules={[{ required: true, message: "Please fill name" }]}
                  style={{ marginBottom: 0 }}>
                  <Input
                    placeholder="Sender Name"
                    autoComplete="off"
                    status={validationError ? "error" : ""}
                    title={validationError || ""}
                  />
                </Form.Item>
              </div>
              <div style={{ flex: 1, marginRight: "10px", width: "100%", height: "40px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px"
                  }}>
                  Company Name
                </label>
                <Form.Item
                  name="senderCompanyName"
                  rules={[{ required: true, message: "Please fill company name" }]}
                  style={{ marginBottom: 0 }}>
                  <Input
                    placeholder="Company Name"
                    autoComplete="off"
                    status={validationError ? "error" : ""}
                    title={validationError || ""}
                  />
                </Form.Item>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "30px"
              }}>
              <div style={{ flex: 1, marginRight: "10px", width: "100%", height: "40px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px"
                  }}>
                  Phone Number
                </label>
                <Form.Item
                  name="senderPhoneNumber"
                  rules={[{ validator: validatePhoneAndFax }]}>
                  <Input
                    placeholder="Phone Number"
                    autoComplete="off"
                    status={validationError ? "error" : ""}
                    title={validationError || ""}
                    onChange={e => {
                      const formattedValue = formatPhoneNumber(e.target.value);
                      form.setFieldsValue({ senderPhoneNumber: formattedValue });
                    }}
                  />
                </Form.Item>
              </div>
              <div style={{ flex: 1, marginRight: "10px", width: "100%", height: "40px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px"
                  }}>
                  Fax Number
                </label>
                <Form.Item
                  name="senderFaxNumber"
                  rules={[{ validator: validatePhoneAndFax }]}
                  style={{ marginBottom: "0" }}>
                  <Input
                    placeholder="Fax Number"
                    status={validationError ? "error" : ""}
                    title={validationError || ""}
                    autoComplete="off"
                    onChange={e => {
                      const formattedValue = formatPhoneNumber(e.target.value);
                      form.setFieldsValue({ senderFaxNumber: formattedValue });
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </Card>

          <Card
            style={{
              marginBottom: "20px",
              border: "1px solid #ddd",
              padding: "10px",
              width: "100%"
            }}
            styles={{ body: { padding: 0 } }}>
            <h3 style={{ marginBottom: "12px" }}>
              <strong>Recipient Information</strong>
            </h3>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "40px"
              }}>
              <div style={{ flex: 1, marginRight: "10px", width: "100%", height: "40px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px"
                  }}>
                  Name
                </label>
                <Form.Item
                  name="recipientName"
                  rules={[{ required: true, message: "Please fill in the Name" }]}
                  style={{ marginBottom: "0" }}>
                  <Input
                    placeholder="Recipient Name"
                    autoComplete="off"
                    status={validationError ? "error" : ""}
                    title={validationError || ""}
                  />
                </Form.Item>
              </div>
              <div style={{ flex: 1, marginRight: "10px", width: "100%", height: "40px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px"
                  }}>
                  Company Name
                </label>
                <Form.Item name="recipientCompanyName" style={{ marginBottom: "0" }}>
                  <Input
                    placeholder="Company Name"
                    status={validationError ? "error" : ""}
                    title={validationError || ""}
                    autoComplete="off"
                    style={{ border: "1px solid #7777774D", width: "100%" }}
                  />
                </Form.Item>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "30px"
              }}>
              <div style={{ flex: "1", marginRight: "10px", width: "100%", height: "40px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px"
                  }}>
                  Phone Number
                </label>
                <Form.Item
                  name="recipientPhoneNumber"
                  rules={[{ validator: validatePhoneAndFax }]}>
                  <Input
                    placeholder="Phone Number"
                    status={validationError ? "error" : ""}
                    title={validationError || ""}
                    autoComplete="off"
                    onChange={e => {
                      const formattedValue = formatPhoneNumber(e.target.value);
                      form.setFieldsValue({ recipientPhoneNumber: formattedValue });
                    }}
                  />
                </Form.Item>
              </div>
              <div style={{ flex: "1", marginRight: "10px", width: "100%", height: "40px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px"
                  }}>
                  Fax Number
                </label>
                <Form.Item
                  name="recipientFaxNumber"
                  rules={[
                    { validator: validatePhoneAndFax },
                    { required: true, message: "Please fill the Number" }
                  ]}
                  style={{ marginBottom: "0" }}>
                  <Input
                    placeholder="Fax Number"
                    status={validationError ? "error" : ""}
                    title={validationError || ""}
                    autoComplete="off"
                    onChange={e => {
                      const formattedValue = formatPhoneNumber(e.target.value);
                      form.setFieldsValue({ recipientFaxNumber: formattedValue });
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </Card>
        </div>
        <Card
          style={{ marginBottom: "20px", border: "1px solid #ddd", padding: "10px" }}
          styles={{ body: { padding: 0 } }}>
          <h3 style={{ marginBottom: "12px" }}>
            <strong>Additional Information</strong>
          </h3>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                flex: "1",
                marginRight: "10px",
                width: "100%",
                height: "40px",
                marginBottom: "30px"
              }}>
              <label
                style={{
                  fontSize: "12px",
                  display: "block",
                  marginBottom: "4px"
                }}>
                Subject
              </label>
              <Form.Item name="subject" style={{ marginBottom: "0" }}>
                <Input
                  placeholder="Subject"
                  status={validationError ? "error" : ""}
                  title={validationError || ""}
                  autoComplete="off"
                  style={{ border: "1px solid #7777774D", width: "100%" }}
                />
              </Form.Item>
            </div>
            <div style={{ flex: "1", marginRight: "10px", width: "100%", height: "100%" }}>
              <label
                style={{
                  fontSize: "12px",
                  display: "block",
                  marginBottom: "4px"
                }}>
                Notes
              </label>
              <Form.Item name="notes">
                <Input.TextArea
                  rows={4}
                  placeholder="Notes..."
                  status={validationError ? "error" : ""}
                  title={validationError || ""}
                  onChange={handleNotesChange}
                  autoComplete="off"
                  style={{ border: "1px solid #7777774D", width: "100%" }}
                />
              </Form.Item>
            </div>
            <div style={{ flex: "1", marginRight: "10px", width: "100%", height: "100%" }}>
              <label
                style={{
                  fontSize: "12px",
                  display: "block",
                  marginBottom: "4px"
                }}>
                Confidentiality Notice
              </label>
              <Form.Item name="confidentialityNotice">
                <Input.TextArea
                  rows={4}
                  autoComplete="off"
                  status={validationError ? "error" : ""}
                  title={validationError || ""}
                  style={{ border: "1px solid #7777774D", width: "100%" }}
                />
              </Form.Item>
            </div>
          </div>
        </Card>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form>
    </Modal>
  );
};

export default NewFaxModal;
